import * as React from "react"

export interface UseAutocompleteProps<TItem> {
  /**
   * 검색할 아이템 목록
   */
  items: TItem[]
  /**
   * 아이템을 식별하는 함수
   */
  identify: (item: TItem) => string
  /**
   * 검색 시 호출되는 함수
   */
  onSearch: (value: string) => TItem[]
  /**
   * 스크롤 영역
   */
  scrollAreaRef: React.RefObject<HTMLElement> | null
  /**
   * 검색 쿼리
   */
  query?: string
  /**
   * 최대 선택 가능한 아이템 수
   */
  maxSelectableCount?: number
  /**
   * 초기 선택 아이템
   */
  initialValue?: TItem | TItem[] | null
  /**
   * 아이템 선택 시 호출되는 함수
   */
  onSelect?: (item: TItem | null) => void
  /**
   * 검색 초기화 시 호출되는 함수
   */
  onClear?: () => void
  /**
   * 검색하여 List가 없는 경우 Add UI가 노출되고 Click/Enter로 추가되는 함수
   */
  onClickAdd?: (value: string) => void
  /**
   * 키보드 이벤트 핸들러
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

interface UseAutocompleteReturn<TItem> {
  searchText: string
  setSearchText: (value: string) => void
  searchedItems: TItem[]
  activeKey: number
  setActiveKey: (index: number) => void
  isOpened: boolean
  setOpened: (focused: boolean) => void
  handleValueChange: (value: string) => void
  handleSelectItem: (item: TItem) => void
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleFocus: () => void
  selectedItems: TItem[]
  hasExactMatchedIndex: boolean
  handleItemRef: (ref: HTMLElement | null, key: number) => void
}

export const useAutocomplete = <TItem>({
  query,
  items,
  identify,
  scrollAreaRef,
  maxSelectableCount = 1,
  initialValue = null,
  onSelect,
  onClear,
  onSearch,
  onClickAdd,
  onKeyDown,
}: UseAutocompleteProps<TItem>): UseAutocompleteReturn<TItem> => {
  const itemRefs = React.useRef<Map<number, HTMLElement>>(new Map())
  const [searchText, setSearchText] = React.useState(query || "")
  const [searchedItems, setSearchedItems] = React.useState<TItem[]>(items)
  const [activeKey, setActiveKey] = React.useState(-1)
  const [isOpened, setOpened] = React.useState(false)
  const [selectedItems, setSelectedItems] = React.useState<TItem[]>(() => {
    if (!initialValue) {
      return []
    }
    const isArray = Array.isArray(initialValue)
    return isArray ? initialValue.slice(0, maxSelectableCount) : [initialValue]
  })

  React.useEffect(
    function initializeSearchedItems() {
      setSearchedItems(items)
      setActiveKey(-1)
    },
    [items],
  )

  React.useEffect(
    function initializeSearchText() {
      setSearchText(query || "")
    },
    [query],
  )

  React.useEffect(() => {
    if (activeKey >= 0) {
      const activeRef = itemRefs.current.get(activeKey)
      if (activeRef && scrollAreaRef?.current) {
        const container = scrollAreaRef.current
        const containerRect = container.getBoundingClientRect()
        const activeItemRect = activeRef.getBoundingClientRect()
        const isItemAbove = activeItemRect.top < containerRect.top
        const isItemBelow = activeItemRect.bottom > containerRect.bottom
        if (isItemAbove || isItemBelow) {
          const prevScrollTop = container.scrollTop
          const topDiff = activeItemRect.top - containerRect.top
          const middlePosition = (containerRect.height - activeItemRect.height) / 2
          const position = prevScrollTop + topDiff - middlePosition
          container.scrollTo({
            top: position,
            behavior: "smooth",
          })
        }
      }
    }
  }, [activeKey, scrollAreaRef])

  const handleItemRef = (ref: HTMLElement | null, key: number) => {
    if (ref) {
      itemRefs.current.set(key, ref)
    } else {
      itemRefs.current.delete(key)
    }
  }

  const getExactMatchedIndex = (items: TItem[], value: string) => {
    const matchedIndex = items.findIndex(item => {
      return identify(item).toLowerCase() === value.toLowerCase()
    })
    return matchedIndex
  }

  const handleValueChange = (value: string) => {
    setSearchText(value)
    const searchedItems = onSearch?.(value)
    if (searchedItems) {
      setSearchedItems(searchedItems)
    }

    const exactMatchedIndex = getExactMatchedIndex(searchedItems, value)
    setActiveKey(exactMatchedIndex)
  }

  const handleSelectItem = (item: TItem) => {
    if (maxSelectableCount === 1) {
      setSelectedItems([item])
    } else {
      const newSelectedItems = [...selectedItems, item].slice(0, maxSelectableCount)
      setSelectedItems(newSelectedItems)
    }
    onSelect?.(item)
    setSearchText(identify(item))
    setOpened(false)
  }

  const handleClear = () => {
    setOpened(false)
    handleValueChange("")
    setActiveKey(-1)
    onClear?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e)

    switch (e.key) {
      case "Enter": {
        e.preventDefault()
        if (e.nativeEvent.isComposing) return

        if (searchText.length > 0 && activeKey < 0) {
          const exactMatchIndex = getExactMatchedIndex(items, searchText)
          if (exactMatchIndex >= 0) {
            handleSelectItem(items[exactMatchIndex])
            return
          }

          if (onClickAdd) {
            onClickAdd(searchText)
            setOpened(false)
            return
          }
          return
        }

        if (activeKey >= 0) {
          handleSelectItem(searchedItems[activeKey])
        }

        break
      }
      case "ArrowUp": {
        e.preventDefault()
        if (activeKey > 0) {
          setActiveKey(prevKey => prevKey - 1)
        }
        break
      }
      case "ArrowDown": {
        e.preventDefault()
        if (e.keyCode === 229) return
        if (e.nativeEvent.isComposing) return
        if (!isOpened) {
          setOpened(true)
          return
        }
        if (activeKey < searchedItems.length - 1) {
          setActiveKey(prevKey => prevKey + 1)
        }
        break
      }
      case "Backspace": {
        if (e.nativeEvent.isComposing) return
        if (searchText === "") {
          handleClear()
        }
        break
      }
      case "Escape": {
        e.preventDefault()
        handleClear()
        break
      }
      case "End": {
        e.preventDefault()
        e.stopPropagation()
        break
      }
      default: {
        if (!isOpened) {
          setOpened(true)
        }
        break
      }
    }
  }

  const handleFocus = () => {
    handleValueChange("")
    setActiveKey(-1)
    onClear?.()
    setOpened(true)
  }

  const hasExactMatchedIndex = getExactMatchedIndex(searchedItems, searchText) >= 0

  return {
    searchText,
    setSearchText,
    searchedItems,
    activeKey,
    setActiveKey,
    isOpened,
    setOpened,
    handleValueChange,
    handleSelectItem,
    handleKeyDown,
    handleFocus,
    selectedItems,
    hasExactMatchedIndex,
    handleItemRef,
  }
}
